import { Component, Input, Optional, HostListener, OnInit, OnDestroy } from '@angular/core';
import { ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { ModalController } from '@ionic/angular';

import { Place } from '@app/modules/model/place';
import { User } from '@app/modules/model/user';
import { languageService, languageSupport } from '@app/modules/common/language';
import { syncService } from '@app/modules/sync';
import { keyboardWedgeService } from '@app/modules/common/keyboard-wedge';
import { denporaClockQueueService } from '@app/modules/denpora-clock-queue';
import { waiterOfflineIdentService } from '@app/modules/waiter-offline-ident';
import { Subscription } from 'rxjs';

/**
 * Modal de identificación por PIN del camarero. Reutiliza el numpad de
 * new_camareros, pero RESUELVE dentro contra `place.waiters` (PIN único por
 * local) y devuelve el Waiter encontrado. En `lockMode` no se puede cancelar
 * (pantalla de bloqueo: hasta que no se mete un PIN válido no se sale).
 */
@Component({
    selector: 'ui-waiter-pin',
    template: `
        <ion-content [scrollY]="false">
            <div class="pin-input">
                <ion-item lines="none" class="pin-header-item">
                    <ion-label>
                        <h2>{{ tr(StepTitleKey) }}</h2>
                        <p>{{ tr(StepSubtitleKey) }}</p>
                    </ion-label>
                    <ion-button *ngIf="!lockMode" [attr.data-id]="'waiterpin-cancel-button'" fill="clear" size="small" [disabled]="clockMode && _busy" (click)="onCancel()" slot="end">
                        <ion-icon name="close" slot="icon-only"></ion-icon>
                    </ion-button>
                </ion-item>

                <!-- FICHAJE SIN CONEXION: el servidor no responde, asi que no se sabe quien ficha
                     ni si entra o sale. Se pregunta y se guarda en el TPV con la hora real; se
                     envia (y el servidor revalida el PIN) al recuperar la conexion. -->
                <div class="clock-offline" *ngIf="_offline">
                    <ion-icon name="cloud-offline-outline"></ion-icon>
                    <p class="who" *ngIf="_offlineName">{{ _offlineName }}</p>
                    <p>{{ tr(_offlineMode === 'pending' ? '@denpora_clock_pending_message' : '@denpora_clock_offline_message') }}</p>
                    <p class="hint" *ngIf="_suggest">{{ tr('@denpora_clock_suggest', [ tr(_suggest.last.action === 'clock_out' ? '@denpora_clock_action_out' : '@denpora_clock_action_in'), _suggestTime ]) }}</p>
                    <ion-button [attr.data-id]="'waiterpin-offline-in'" expand="block" size="large" color="success" [fill]="(_suggest && _suggest.expected !== 'clock_in') ? 'outline' : 'solid'" (click)="onChoose('clock_in')">
                        <ion-icon slot="start" name="log-in-outline"></ion-icon>{{ tr('@denpora_clock_in_button') }}
                    </ion-button>
                    <ion-button [attr.data-id]="'waiterpin-offline-out'" expand="block" size="large" color="warning" [fill]="(_suggest && _suggest.expected !== 'clock_out') ? 'outline' : 'solid'" (click)="onChoose('clock_out')">
                        <ion-icon slot="start" name="log-out-outline"></ion-icon>{{ tr('@denpora_clock_out_button') }}
                    </ion-button>
                    <ion-button [attr.data-id]="'waiterpin-offline-back'" expand="block" fill="clear" (click)="onBack()">
                        {{ tr('@denpora_clock_offline_back') }}
                    </ion-button>
                </div>

                <div class="pin-display-container" *ngIf="!_offline">
                    <div class="pin-display" [class.error]="_error">
                        <div class="pin-display-content">
                            <span class="pin-value" [class.error]="_error" [class.empty]="_pin.length === 0">
                                {{ _pin.length > 0 ? '*'.repeat(_pin.length) : 'PIN' }}
                            </span>
                            <p *ngIf="_error" class="error-message-inline">{{ _error }}</p>
                        </div>
                    </div>
                </div>

                <!-- BLOQUEO SIN CONEXION: quien no pueda identificarse aqui aun puede fichar. -->
                <ion-button class="lock-clock" *ngIf="CanClockFromLock && !_offline" [attr.data-id]="'waiterpin-lock-clock'" expand="block" fill="outline" (click)="onOpenClock()">
                    <ion-icon slot="start" name="time-outline"></ion-icon>{{ tr('@place_waiter_pin_clock') }}
                </ion-button>

                <div class="numpad" *ngIf="!_offline">
                    <ion-grid class="no-select">
                        <ion-row>
                            <ion-col size="4" role="button" tabindex="0" [attr.data-id]="'waiterpin-numpad_7'" aria-label="7" (click)="onKey(7)" (keydown.enter)="onKey(7)" (keydown.space)="onKey(7)"><div class="numkey">7</div></ion-col>
                            <ion-col size="4" role="button" tabindex="0" [attr.data-id]="'waiterpin-numpad_8'" aria-label="8" (click)="onKey(8)" (keydown.enter)="onKey(8)" (keydown.space)="onKey(8)"><div class="numkey">8</div></ion-col>
                            <ion-col size="4" role="button" tabindex="0" [attr.data-id]="'waiterpin-numpad_9'" aria-label="9" (click)="onKey(9)" (keydown.enter)="onKey(9)" (keydown.space)="onKey(9)"><div class="numkey">9</div></ion-col>
                        </ion-row>
                        <ion-row>
                            <ion-col size="4" role="button" tabindex="0" [attr.data-id]="'waiterpin-numpad_4'" aria-label="4" (click)="onKey(4)" (keydown.enter)="onKey(4)" (keydown.space)="onKey(4)"><div class="numkey">4</div></ion-col>
                            <ion-col size="4" role="button" tabindex="0" [attr.data-id]="'waiterpin-numpad_5'" aria-label="5" (click)="onKey(5)" (keydown.enter)="onKey(5)" (keydown.space)="onKey(5)"><div class="numkey">5</div></ion-col>
                            <ion-col size="4" role="button" tabindex="0" [attr.data-id]="'waiterpin-numpad_6'" aria-label="6" (click)="onKey(6)" (keydown.enter)="onKey(6)" (keydown.space)="onKey(6)"><div class="numkey">6</div></ion-col>
                        </ion-row>
                        <ion-row>
                            <ion-col size="4" role="button" tabindex="0" [attr.data-id]="'waiterpin-numpad_1'" aria-label="1" (click)="onKey(1)" (keydown.enter)="onKey(1)" (keydown.space)="onKey(1)"><div class="numkey">1</div></ion-col>
                            <ion-col size="4" role="button" tabindex="0" [attr.data-id]="'waiterpin-numpad_2'" aria-label="2" (click)="onKey(2)" (keydown.enter)="onKey(2)" (keydown.space)="onKey(2)"><div class="numkey">2</div></ion-col>
                            <ion-col size="4" role="button" tabindex="0" [attr.data-id]="'waiterpin-numpad_3'" aria-label="3" (click)="onKey(3)" (keydown.enter)="onKey(3)" (keydown.space)="onKey(3)"><div class="numkey">3</div></ion-col>
                        </ion-row>
                        <ion-row>
                            <ion-col size="4" role="button" tabindex="0" [attr.data-id]="'waiterpin-numpad_0'" aria-label="0" (click)="onKey(0)" (keydown.enter)="onKey(0)" (keydown.space)="onKey(0)"><div class="numkey">0</div></ion-col>
                            <ion-col size="4" role="button" [attr.data-id]="'waiterpin-numpad_go'" [attr.tabindex]="_pin.length >= minLength ? 0 : -1" [attr.aria-disabled]="_pin.length < minLength" [attr.aria-label]="tr('@place_waiter_pin_ok')" [ngClass]="{'disabled': _pin.length < minLength}" (click)="_pin.length >= minLength && onConfirm()" (keydown.enter)="_pin.length >= minLength && onConfirm()">
                                <div class="numkey return"><ion-icon color="success" name="checkmark-circle-outline"></ion-icon></div>
                            </ion-col>
                            <ion-col size="4" role="button" tabindex="0" [attr.data-id]="'waiterpin-numpad_B'" [attr.aria-label]="tr('@place_waiter_pin_del')" (click)="onClear()" (keydown.enter)="onClear()"><div class="numkey"><ion-icon name="backspace-outline"></ion-icon></div></ion-col>
                        </ion-row>
                    </ion-grid>
                </div>
            </div>
        </ion-content>
    `,
    styleUrls: ['./ui-waiter-pin.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class UiWaiterPin extends languageSupport implements OnInit, OnDestroy {
    @Input() place: Place = null;
    @Input() lockMode: boolean = false;     // true: bloqueo (no se puede cancelar)
    @Input() captureMode: boolean = false;  // true: solo CAPTURAR el PIN (ponerlo en
                                            // una ficha), sin resolver contra el server
    @Input() changeMode: boolean = false;   // true: CAMBIO de PIN multi-paso
                                            // (actual -> nuevo -> repite); devuelve {old, pin}
    @Input() needsOld: boolean = false;     // en changeMode: pedir el PIN actual (hay uno)
    @Input() checkPlace: number = null;     // si está, comprobar DISPONIBILIDAD del PIN
                                            // nuevo en este local (avisar "ya en uso")
    @Input() checkExclude: number = 0;      // objid del STAFF que se edita (su PIN actual
                                            // no cuenta como "cogido")
    @Input() clockMode: boolean = false;    // true: FICHAJE en Denpora: el server resuelve
                                            // el PIN y ficha por el empleado; devuelve
                                            // { user, clock } (ver denpora/clock.php)

    maxLength = 8;
    minLength = 4;
    _pin = '';
    _error = '';
    _busy = false;
    _step: 'old' | 'new' | 'confirm' = 'new';    // paso actual del cambio
    _oldval = '';
    _newval = '';
    _closed = false;    // el modal ya se cerró/canceló: no hacer un dismiss tardío
                        // (cerraría un modal MÁS NUEVO con el resultado de este)
    // Referencia a NUESTRO propio modal (somos el top al arrancar): los dismiss llegan
    // tras una petición al server y `modalCtrl.dismiss()` cerraría el top ACTUAL, que
    // puede ser el modal de BLOQUEO abierto encima mientras tanto (Copilot #1819).
    private _ownModal: any = null;
    // El propio <ion-modal> que nos presenta: Ionic Angular lo inyecta como `modal` en la
    // instancia (componentProps[elementReferenceKey], ionic-angular 6.7) ANTES de ngOnInit.
    // Es la referencia fiable; getTop() es "el de mas arriba AHORA" y, si otro modal se
    // presenta casi a la vez (bloqueo mientras se abre el fichaje), puede ser el otro
    // (Bugbot #1824). getTop() queda solo como respaldo si el componente no viene de un modal.
    @Input() modal: any = null;
    private _dismissSelf(data?: any){
        if (this.modal && (typeof this.modal.dismiss === 'function')){
            return this.modal.dismiss(data);
        }
        if (this._ownModal && (typeof this._ownModal.dismiss === 'function')){
            return this._ownModal.dismiss(data);
        }
        if (this.modalCtrl){
            return this.modalCtrl.dismiss(data);
        }
        return Promise.resolve(false);
    }

    private _rfidSub: Subscription = null;
    private _lastTag = '';
    private _lastTagTs = 0;
    private static readonly TAG_REPEAT_MS = 3000;

    // FICHAJE SIN CONEXIÓN (clockMode): el servidor no responde -> se pregunta ENTRADA o
    // SALIDA y se encola con la credencial y la hora real (ver denpora-clock-queue.ts).
    _offline = false;
    _offlineMode: 'network' | 'denpora' | 'pending' = 'network';
    _offlineName = '';                  // con Denpora caído el servidor ya identificó al camarero
    _suggest: { expected: 'clock_in' | 'clock_out', last: { action: string, at: string } } = null;
    _suggestTime = '';
    private _offlineCredential: { pin?: string, rfid?: string } = null;
    private _offlineUser: any = null;
    private _noServer = false;          // la última identificación no llegó al servidor

    constructor(lang: languageService, change: ChangeDetectorRef, private sync: syncService, @Optional() private modalCtrl: ModalController, private wedge: keyboardWedgeService, private clockQueue: denporaClockQueueService, private offlineIdent: waiterOfflineIdentService){
        super(lang, change);
    }

    async ngOnInit(): Promise<void> {
        if (this.changeMode){
            this._step = this.needsOld ? 'old' : 'new';
        }
        // PULSERA RFID: con el modal de identificación/bloqueo abierto, acercar la
        // pulsera al lector identifica igual que el PIN (sin teclear nada); en el de
        // FICHAJE (Denpora) ficha igual que el PIN. En los modos de captura/cambio de
        // PIN no se identifica a nadie, pero los dos primeros dígitos de la ráfaga (los
        // que el despachador aún no puede tragar) han llegado al numpad: se limpian.
        let _identify = !this.captureMode && !this.changeMode && !this.clockMode;
        this._rfidSub = this.wedge.OnTag.subscribe((tag: string) => {
            this._pin = '';     // lo que el lector haya colado en el numpad
            this._error = '';
            // Relecturas del lector con la pulsera encima: una sola por acercamiento.
            let _now = Date.now();
            if ((tag === this._lastTag) && ((_now - this._lastTagTs) < UiWaiterPin.TAG_REPEAT_MS)){
                this._lastTagTs = _now;
                return;
            }
            this._lastTag = tag;
            this._lastTagTs = _now;
            if (this._offline){
                this._chng.markForCheck();      // ya hay un fichaje sin red a medio elegir
            }
            else if (this.clockMode){
                this._clock({ rfid: tag }).catch(() => {});
            }
            else if (_identify){
                this._identify({ rfid: tag }).catch(() => {});
            }
            else {
                this._chng.markForCheck();
            }
        });
        if (this.modalCtrl && !this.modal){
            try { this._ownModal = (await this.modalCtrl.getTop()) || null; } catch(e){ this._ownModal = null; }
        }
    }

    // Pantalla de bloqueo sin servidor con Denpora activo: se puede fichar sin identificarse.
    get CanClockFromLock(): boolean {
        return this.lockMode && !!this.place && this.place.DenporaEnabled && (this._noServer || !this.sync.ServerReachable);
    }

    // El fichaje lo abre ui-place al cerrarse este modal; al terminar vuelve el bloqueo.
    onOpenClock(): void {
        if (this._busy || this._closed){
            return;
        }
        this._pin = '';
        this._closed = true;
        this._dismissSelf({ openClock: true });
    }

    // Título/subtítulo según el paso (cambio de PIN) o el modo.
    get StepTitleKey(): string {
        if (this.changeMode){
            if (this._step === 'old'){ return '@place_waiter_pin_old_title'; }
            if (this._step === 'confirm'){ return '@place_waiter_pin_repeat_title'; }
            return '@place_waiter_pin_new_title';
        }
        if (this.clockMode){
            return this._offline ? '@denpora_clock_offline_title' : '@denpora_clock_title';
        }
        return this.captureMode ? '@place_waiter_pin_new_title' : '@place_waiter_pin_title';
    }
    get StepSubtitleKey(): string {
        if (this.changeMode){
            if (this._step === 'old'){ return '@place_waiter_pin_old_subtitle'; }
            if (this._step === 'confirm'){ return '@place_waiter_pin_repeat_subtitle'; }
            return '@place_waiter_pin_new_subtitle';
        }
        if (this.clockMode){
            return this._offline ? '@denpora_clock_offline_subtitle' : '@denpora_clock_subtitle';
        }
        return this.captureMode ? '@place_waiter_pin_new_subtitle' : '@place_waiter_pin_subtitle';
    }

    // Operable por TECLADO (en modo bloqueo no se puede cancelar: si no, se
    // atrapaba a usuarios de teclado / tecnología de asistencia). Dígitos 0-9,
    // Backspace borra, Enter confirma.
    @HostListener('document:keydown', ['$event'])
    onKeydown(ev: KeyboardEvent){
        if (this._offline){
            // Paso entrada/salida sin red: solo Escape (vuelve al numpad); los dígitos
            // de una pulsera o del teclado no son un PIN aquí.
            if (ev.key === 'Escape'){
                this.onBack();
                ev.preventDefault();
            }
            return;
        }
        if (ev.key >= '0' && ev.key <= '9'){
            this.onKey(parseInt(ev.key, 10));
            ev.preventDefault();
        }
        else if (ev.key === 'Backspace'){
            this.onClear();
            ev.preventDefault();
        }
        else if (ev.key === 'Enter'){
            // Si el foco está en una tecla del numpad, su propio (keydown.enter)
            // ya la maneja: NO confirmar también aquí (si no, a los 4 dígitos se
            // enviaba el PIN e impedía meter 5-8). Solo confirmamos con Enter
            // desde FUERA del numpad.
            let _t = ev.target as HTMLElement;
            if (!_t || !_t.closest || !_t.closest('.numpad')){
                this.onConfirm();
                ev.preventDefault();
            }
        }
        else if ((ev.key === 'Escape') && !this.lockMode){
            this.onCancel();
            ev.preventDefault();
        }
    }

    onKey(n: number): void {
        if (this._pin.length >= this.maxLength){
            return;
        }
        this._pin += String(n);
        this._error = '';
        this._chng.markForCheck();
    }

    onClear(): void {
        if (this._pin.length > 0){
            this._pin = this._pin.slice(0, -1);
        }
        this._error = '';
        this._chng.markForCheck();
    }

    async onConfirm(): Promise<void> {
        if (this._pin.length < this.minLength || this._busy){
            return;
        }

        // Modo CAMBIO de PIN: multi-paso actual -> nuevo -> repite. Devuelve {old,pin}.
        if (this.changeMode){
            let _v = this._pin;
            this._pin = '';
            this._error = '';
            if (this._step === 'old'){
                this._oldval = _v;
                this._step = 'new';
            }
            else if (this._step === 'new'){
                // Avisar YA si el PIN nuevo ya lo tiene otro empleado (en vez del
                // error genérico al guardar). El cliente no tiene los hashes: lo
                // resuelve el server (pincheck.php). Si no se puede comprobar (sin
                // permiso / offline) NO bloqueamos: el server valida al guardar.
                if (this.checkPlace){
                    this._busy = true;
                    this._chng.markForCheck();
                    let _free = await this._isAvailable(_v);
                    this._busy = false;
                    if (!_free){
                        this._error = this.tr('@place_waiter_pin_taken');
                        this._chng.markForCheck();
                        return;     // seguir en el paso 'new' con el aviso
                    }
                }
                this._newval = _v;
                this._step = 'confirm';
            }
            else {  // confirm
                if (_v !== this._newval){
                    this._error = this.tr('@place_waiter_pin_nomatch');
                    this._newval = '';
                    this._step = 'new';     // los dos nuevos no coinciden: reintentar
                }
                else if (!this._closed && this.modalCtrl){
                    this._closed = true;
                    this._dismissSelf({ old: this._oldval, pin: this._newval });
                }
            }
            this._chng.markForCheck();
            return;
        }

        // Modo captura: solo DEVOLVER el PIN tecleado (para ponerlo en una ficha).
        // No se resuelve contra el server (eso es identificación, no alta).
        if (this.captureMode){
            let _captured = this._pin;
            // Aviso temprano de PIN ya en uso (ver paso 'new' del cambio).
            if (this.checkPlace){
                this._busy = true;
                this._chng.markForCheck();
                let _free = await this._isAvailable(_captured);
                this._busy = false;
                if (!_free){
                    this._error = this.tr('@place_waiter_pin_taken');
                    this._chng.markForCheck();
                    return;
                }
            }
            this._pin = '';
            if (this.modalCtrl){
                this._dismissSelf({ pin: _captured });
            }
            return;
        }

        if (!this.place){
            return;
        }

        // Modo FICHAJE (Denpora): mismo PIN, pero el server ficha por el empleado.
        if (this.clockMode){
            await this._clock({ pin: this._pin });
            return;
        }

        await this._identify({ pin: this._pin });
    }

    // Identificación SERVER-SIDE por PIN o por pulsera RFID (la credencial va en
    // el cuerpo, nunca en la URL ni se resuelve en el cliente). Resuelve el USER
    // efectivo (camarero u owner) y cierra el modal devolviéndolo.
    private async _identify(body: { pin?: string, rfid?: string }): Promise<void> {
        if (this._busy || !this.place){
            return;
        }
        this._busy = true;
        this._chng.markForCheck();
        try {
            let _data: any = await this.sync.DoRequest(
                'model/staffpin.php',
                { place: this.place.objid },
                JSON.stringify(body)
            );

            let _uobjid = (_data && (_data['errorcode'] == 0)) ? _data['user'] : null;
            let _match = this._matchUser(_uobjid);

            if (_match){
                this.offlineIdent.Remember(this.place.objid, _uobjid, body);
                this._pin = '';
                // Si el modal ya se canceló mientras el DoRequest estaba en vuelo,
                // NO hacer dismiss: cerraría el modal que esté ahora encima con el
                // resultado de esta petición (identificaría en un modal ajeno).
                if (!this._closed && this.modalCtrl){
                    this._closed = true;
                    this._dismissSelf(_match);
                }
            }
            else {
                // PIN/pulsera incorrectos (o el usuario no está ni como camarero ni
                // como owner: no identificamos para no caer a permisos de owner).
                if (_data && (_data['errorcode'] == 3)){
                    this.offlineIdent.Forget(this.place.objid, body);
                }
                this._error = this.tr(body.rfid ? '@place_waiter_rfid_invalid' : '@place_waiter_pin_invalid');
                this._pin = '';
            }
        }
        catch(e){
            // Sin conexión: solo vale lo verificado antes con red en este dispositivo.
            let _match = this._matchUser(await this.offlineIdent.Find(this.place.objid, body));
            this._pin = '';
            if (_match){
                if (!this._closed && this.modalCtrl){
                    this._closed = true;
                    this._dismissSelf(_match);
                }
            }
            else {
                this._noServer = true;
                this._error = this.tr('@place_waiter_pin_offline');
            }
        }
        finally {
            this._busy = false;
            this._chng.markForCheck();
        }
    }

    // Resuelve el USER efectivo del local a partir de su objid: puede ser un camarero
    // (STAFF) o el OWNER del local (que no es STAFF). null si no está en el modelo.
    private _matchUser(_uobjid: any): User {
        return this.place ? this.place.FindUserByObjid(_uobjid) : null;
    }

    // FICHAJE en Denpora: el PIN va al server (denpora/clock.php), que lo resuelve con
    // la misma lógica que staffpin.php y ficha en Denpora por el empleado (por email).
    // El token de Denpora nunca llega al cliente. Devuelve { user, clock } al cerrar.
    private async _clock(credential: { pin?: string, rfid?: string }): Promise<void> {
        // _closed: ya se fichó y el modal se está cerrando; una relectura de la pulsera
        // (o un Enter tardío) no debe volver a llamar al server y alternar el fichaje.
        if (this._busy || this._closed || !this.place){
            return;
        }
        this._busy = true;
        this._chng.markForCheck();
        try {
            // ORDEN: si hay fichajes pendientes de un corte, se envían ANTES. Un fichaje en
            // vivo no puede adelantarse: alternaría sobre un estado que aún no incluye lo
            // pendiente (una salida se registraría como entrada y la entrada encolada
            // acabaría en conflicto). Si tras intentarlo sigue quedando algo, este va
            // DETRÁS en la cola.
            // Solo cuentan los pendientes de ESTE local: otro local con su integración
            // rota no puede bloquear los fichajes de este.
            if (this.clockQueue.CountFor(this.place.objid) > 0){
                await this.clockQueue.Flush();
                // El modal pudo cerrarse/destruirse mientras se esperaba: no se ficha nada.
                if (this._closed){
                    return;
                }
                if (this.clockQueue.CountFor(this.place.objid) > 0){
                    this._offlineStep(credential, 'pending', null);
                    return;
                }
            }

            let _data: any = await this.sync.DoRequest(
                'denpora/clock.php',
                { place: this.place.objid },
                JSON.stringify(credential)
            );

            if (_data && (_data['errorcode'] == 0) && _data['clock']){
                let _match = this._matchUser(_data['user']);
                this.clockQueue.NoteClock(_data['user'], _data['clock']['action']);
                this._pin = '';
                if (!this._closed && this.modalCtrl){
                    this._closed = true;
                    this._dismissSelf({ user: _match, clock: _data['clock'] });
                }
            }
            else if (_data && (_data['reason'] == 'offline')){
                // Denpora caído (nuestro servidor OK y el PIN ya validado): se encola igual
                // que sin red, con la hora real; se enviará cuando Denpora responda. El
                // servidor dice QUIÉN es: se enseña su nombre y se sugiere la acción.
                this._offlineStep(credential, 'denpora', _data['user']);
            }
            else {
                this._error = this._clockError(_data);
                this._pin = '';
            }
        }
        catch(e){
            // Sin conexión con NUESTRO servidor: no se puede saber quién ficha ni si entra o
            // sale. Se pregunta y se guarda en el TPV (ver denpora-clock-queue.ts).
            this._offlineStep(credential, 'network', null);
        }
        finally {
            this._busy = false;
            this._chng.markForCheck();
        }
    }

    // Paso "sin conexión": elegir ENTRADA/SALIDA para encolar el fichaje con la hora real.
    private _offlineStep(credential: { pin?: string, rfid?: string }, mode: 'network' | 'denpora' | 'pending', user: any){
        if (this._closed){
            return;
        }
        this._offlineCredential = credential;
        this._offlineMode = mode;
        this._offlineUser = user || null;
        let _match: any = user ? this._matchUser(user) : null;
        let _waiter: any = (user && this.place && this.place.waiters) ? this.place.waiters.find((w: any) => w.IsValid && w.user && (String(w.user.objid) == String(user))) : null;
        this._offlineName = (_waiter && _waiter.alias) || (_match ? _match.fullname : '') || '';
        // Ayuda, no decisión: lo contrario de su último fichaje en ESTE TPV si es
        // reciente. Si elige mal, Denpora responde 409 y NO registra lo contrario.
        this._suggest = this.clockQueue.Suggest(user);
        this._suggestTime = '';
        if (this._suggest){
            let _d = new Date(this._suggest.last.at);
            this._suggestTime = isNaN(_d.getTime()) ? '' : _d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        }
        this._offline = true;
        this._error = '';
        this._pin = '';
    }

    onChoose(expected: 'clock_in' | 'clock_out'): void {
        if (!this._offline || !this._offlineCredential || this._closed || !this.place){
            return;
        }
        let _queued = this.clockQueue.Enqueue({
            place: this.place.objid,
            user: this._offlineUser,
            pin: this._offlineCredential.pin,
            rfid: this._offlineCredential.rfid,
            expected: expected
        });
        this._offlineCredential = null;
        this._offline = false;
        this._closed = true;
        if (this.modalCtrl){
            this._dismissSelf({ queued: _queued });     // sin credencial (ver _public)
        }
        this._chng.markForCheck();
    }

    // Volver al numpad sin encolar (p.ej. se tecleó mal el PIN y la red ha vuelto).
    onBack(): void {
        this._offlineCredential = null;
        this._offlineUser = null;
        this._suggest = null;
        this._offline = false;
        this._pin = '';
        this._error = '';
        this._chng.markForCheck();
    }

    // Mensaje para el motivo que devuelve denpora/clock.php.
    private _clockError(_data: any): string {
        let _reason = _data ? _data['reason'] : null;
        switch(_reason){
            case 'pin':           return this.tr('@place_waiter_pin_invalid');
            case 'rfid':          return this.tr('@place_waiter_rfid_invalid');
            case 'ratelimit':     return this.tr('@denpora_clock_ratelimit');
            case 'notconfigured': return this.tr('@denpora_clock_notconfigured');
            case 'noemail':       return this.tr('@denpora_clock_noemail');
            case 'offline':       return this.tr('@denpora_clock_offline');
            case 'token':         return this.tr('@denpora_clock_token');
            case 'notfound':      return this.tr('@denpora_clock_notfound');
        }
        // 'conflict' / 'denpora': el texto de Denpora (ya viene en castellano) si lo hay
        let _msg = (_data && _data['message']) ? String(_data['message']) : '';
        return _msg || this.tr('@denpora_clock_error');
    }

    // ¿Está LIBRE este PIN en el local? Lo resuelve el server (el cliente no tiene
    // hashes). Devuelve true si está libre O si no se pudo comprobar (sin permiso /
    // offline / error): en ese caso NO bloqueamos y el server valida al guardar.
    private async _isAvailable(pin: string): Promise<boolean> {
        try {
            let _data: any = await this.sync.DoRequest(
                'model/pincheck.php',
                { place: this.checkPlace },
                JSON.stringify({ pin: pin, exclude: this.checkExclude || 0 })
            );
            if (_data && (_data['errorcode'] == 0)){
                return _data['available'] !== false;
            }
            return true;    // no autorizado / error: no bloquear aquí
        }
        catch(e){
            return true;    // sin conexión: no bloquear aquí
        }
    }

    onCancel(): void {
        if (this.lockMode){
            return;     // en bloqueo no se sale sin PIN válido
        }
        if (this.clockMode && this._busy){
            return;     // fichaje EN VUELO: cerrar no lo cancela en el server y perdería
                        // la confirmación (y un reintento ficharía lo contrario)
        }
        this._pin = '';
        this._error = '';
        this._offline = false;
        this._offlineCredential = null;     // la credencial no sobrevive al modal
        this._closed = true;    // marca: un DoRequest en vuelo ya no debe hacer dismiss
        if (this.modalCtrl){
            this._dismissSelf(null);
        }
    }

    // El modal se destruye por CUALQUIER vía (backdrop, dismiss externo de
    // PlaceScreen al bloquear/deshabilitar/destruir la vista, teardown): marcar
    // _closed para que una respuesta tardía del DoRequest NO haga dismiss (cerraría
    // el modal que esté ahora encima con este resultado).
    ngOnDestroy(): void {
        this._closed = true;
        // Sin esto cada modal descartado seguiría identificando con cada pulsera
        // leída (una petición a staffpin.php por modal muerto).
        if (this._rfidSub){
            this._rfidSub.unsubscribe();
            this._rfidSub = null;
        }
        // Liberar la suscripción al servicio de idioma (languageSupport): sin esto
        // cada modal de PIN descartado queda retenido (con sus campos de PIN) y los
        // cambios de idioma siguen llamando detectChanges() sobre vistas destruidas.
        super.OnDestroy();
    }
}
